import { matchSorter } from "match-sorter";
import { useMemo, useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@volca/ui/components/ui/empty";
import { SearchInput } from "@volca/ui/components/ui/search-input";
import { Stack } from "@volca/ui/components/ui/stack";
import { emptyStateVariants } from "@volca/ui/lib/empty-state";
import { SIDEBAR_MODES, SIDEBAR_SECTIONS } from "@/business-settings/constants";
import { useBusinessSettingsSidebar } from "@/business-settings/context/business-settings-sidebar-context";
import useDelayedAction from "@/core/hooks/use-delayed-action";
import { dayjs } from "@/core/utils/date";
import { createDefaults } from "@/core/utils/loading";
import {
  TextMessageListItem,
  TextMessageListItemSkeleton,
} from "@/text-messages/components/text-message-list-item";
import { usePrefetchCustomerTextMessages } from "@/text-messages/query";
import { IMostRecentTextMessage } from "@/text-messages/text-messages";

const LOADING_TEXT_MESSAGES = createDefaults("placeholder-text-message", 5);

interface TextMessageListProps {
  textMessages?: IMostRecentTextMessage[];
  isLoading?: boolean;
  isError?: boolean;
  onRetry?: () => void;
  emptyMessage?: string;
}

export const TextMessageList = ({
  textMessages = [],
  isLoading = false,
  isError = false,
  onRetry,
  emptyMessage = "No messages yet",
}: TextMessageListProps) => {
  const [search, setSearch] = useState("");

  const {
    updateSidebar,
    state: { selectedItemId },
  } = useBusinessSettingsSidebar();

  const handleSelectTextMessage = (textMessage: IMostRecentTextMessage) => {
    updateSidebar({
      section: SIDEBAR_SECTIONS.textMessages,
      mode: SIDEBAR_MODES.view,
      selectedItemId: textMessage.id,
      isOpen: true,
    });
  };

  const prefetchCustomerTextMessages = usePrefetchCustomerTextMessages();

  const { schedule: schedulePrefetch, cancel: cancelPrefetch } =
    useDelayedAction(prefetchCustomerTextMessages, 250);

  const filteredTextMessages = useMemo(() => {
    if (!search) return textMessages;
    return matchSorter(textMessages, search, {
      keys: [
        (item) => item.customer?.name || "",
        (item) => item.customer?.phone_number || "",
        (item) => item.body || "",
      ],
      threshold: matchSorter.rankings.CONTAINS,
    });
  }, [textMessages, search]);

  const sortedTextMessages = useMemo(
    () =>
      [...filteredTextMessages].sort(
        (a, b) => dayjs(b.created_at).valueOf() - dayjs(a.created_at).valueOf(),
      ),
    [filteredTextMessages],
  );

  return (
    <Stack align="start" className="h-full w-full overflow-hidden" gap={1}>
      <SearchInput
        placeholder="Search messages"
        value={search}
        onChange={(value) => setSearch(value)}
      />
      <div className="h-full w-full overflow-y-auto pr-2">
        <Stack align="start" gap={0} className="min-h-full w-full py-2 pr-4">
          {isError && !isLoading && (
            <Empty
              className={emptyStateVariants({ layout: "collection" })}
              role="alert"
            >
              <EmptyHeader>
                <EmptyTitle>Couldn't load messages</EmptyTitle>
                <EmptyDescription>Please try again.</EmptyDescription>
              </EmptyHeader>
              {onRetry && (
                <EmptyContent>
                  <Button variant="outline" size="sm" onClick={onRetry}>
                    Try again
                  </Button>
                </EmptyContent>
              )}
            </Empty>
          )}
          {sortedTextMessages.length === 0 && !isLoading && !isError && (
            <Empty className={emptyStateVariants({ layout: "collection" })}>
              <EmptyHeader>
                <EmptyDescription>
                  {search.trim()
                    ? "No messages match your search"
                    : emptyMessage}
                </EmptyDescription>
              </EmptyHeader>
              {search.trim() && (
                <EmptyContent>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSearch("")}
                  >
                    Clear search
                  </Button>
                </EmptyContent>
              )}
            </Empty>
          )}
          {isLoading &&
            LOADING_TEXT_MESSAGES.map((t, index) => (
              <TextMessageListItemSkeleton key={t.id} index={index} />
            ))}
          {sortedTextMessages?.map((t) => (
            <TextMessageListItem
              key={t.id}
              textMessage={t}
              isSelected={selectedItemId === t.id}
              onSelect={handleSelectTextMessage}
              onMouseEnter={() => schedulePrefetch(t.customer.id)}
              onMouseLeave={cancelPrefetch}
            />
          ))}
        </Stack>
      </div>
    </Stack>
  );
};
